/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { AxiosError } from 'axios';
import { MotorsportsError, MotorsportsErrorType } from 'services/motorsportsError/MotorsportsError';
import { weatherService } from 'services/weather/WeatherService';
import { sessionApi } from './SessionApi';
import { EventSessionDetailsResponse, Session } from 'models/viewModels';

interface SessionService {
  getSession(sessionId: string): Promise<EventSessionDetailsResponse | undefined>;
  getSessions(eventId: string): Promise<Session[]>;
  getRaceSessionByTrackEventId(trackEventId: string): Promise<EventSessionDetailsResponse | undefined>;
}

class SessionServiceServer implements SessionService {
  // Used to retrieve active session.
  async getSession(eventSessionId: string): Promise<EventSessionDetailsResponse | undefined> {
    if (eventSessionId) {
      return await sessionApi
        .getSession(eventSessionId)
        .then(async (response) => {
          const responseObject = response;

          // Set track time zone if missing.
          const track = responseObject?.track;
          if (
            track &&
            !track.trackTimezone &&
            track.trackLatitude !== undefined &&
            track.trackLongitude !== undefined
          ) {
            const locationPoint = await weatherService.getLocationPoint(track.trackLatitude, track.trackLongitude);
            track.trackTimezone = locationPoint.ianaTimeZone || '';
          }
          return responseObject;
        })
        .catch((error: AxiosError) => {
          if (error.code === 'ERR_BAD_REQUEST') {
            throw new MotorsportsError(eventSessionId, MotorsportsErrorType.SessionNotFound, { cause: error });
          }
          throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
        });
    }

    return undefined;
  }

  // Used to update session status in Select Session table.
  async getSessions(eventId: string): Promise<Session[]> {
    return await sessionApi
      .getSessions(eventId)
      .then(async (response) => response.sessionList)
      .catch((error: Error) => {
        throw Error(`Error updating sessions: ${error.message}`);
      });
  }

  getRaceSessionByTrackEventId = async (trackEventId: string): Promise<EventSessionDetailsResponse | undefined> =>
    sessionApi.getRaceSessionByTrackEventId(trackEventId).then((response) => response).catch((_: AxiosError) => undefined);
}

export const sessionService = new SessionServiceServer();
